import delay from "delay";
import { CrossTabCoordinator } from "../app/session/CrossTabCoordinator";
import { default as getConfig } from "../config";
import { ACCOUNT_SWITCH_STORAGE_KEYS } from "./accountSwitchStorageKeys";
import { ACTIVITY_STORAGE_KEY, StatementOfTaxability, Taxable } from "./constants";

// Config is retrieved lazily inside functions to support runtime configuration
const getLazyConfig = () => {
    const config = getConfig();
    return {
        appEnv: config.appEnv,
        ecmApiHost: config.hostnames.ecmApiHost,
        apiVersion3: config.apiVersion3,
        apiVersion: config.apiVersion,
        assetsPath: config.assetsPath,
    };
};

export const urlToPageMap = {
    "search-certificate": "/search/certificates",
    "search-pending": "/search/pending",
    "search-customer": "/search/customers",
    "search-vendor-certificates": "/search/vendor-certificates",
    review: "/review",
    overview: "/overview",
    retail: "/retail",
    retailSearch: "search",
    retailCustomer: "customer/:id?",
    retailCustomerAndCertificates: "customer-certificates/:id",
    retailAddCertificate: "certificate",
    settings: "/settings",
    customerDetail: "customer/:id?",
    addJob: "/add-job",
    jobDetail: "/jobs/:jobId",
};

export function updateSelectedAttribute(array, value) {
    array?.map(x => {
        const arrayValue = x;
        if (arrayValue?.value?.toString()?.toLowerCase() === value?.toString()?.toLowerCase())
            arrayValue.selected = true;
        else arrayValue.selected = false;
        return arrayValue;
    });
}

export const toBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

/**
 * Repairs truncated PDFs by trimming bytes to the last valid %%EOF marker,
 * stripping any incomplete trailing objects that pdf-lib cannot parse.
 * @param {string} dataUri - base64 data URI of the PDF (e.g. "data:application/pdf;base64,...")
 * @returns {Uint8Array} trimmed PDF bytes
 */
export function trimToLastEof(dataUri) {
    const base64 = dataUri.split(",")[1];
    const raw = atob(base64);
    const bytes = Uint8Array.from(raw, c => c.charCodeAt(0));
    for (let i = bytes.length - 5; i >= 0; i--) {
        if (
            bytes[i] === 0x25 &&
            bytes[i + 1] === 0x25 &&
            bytes[i + 2] === 0x45 &&
            bytes[i + 3] === 0x4f &&
            bytes[i + 4] === 0x46
        ) {
            let end = i + 5;
            if (end < bytes.length && bytes[end] === 0x0d) end++;
            if (end < bytes.length && bytes[end] === 0x0a) end++;
            return bytes.slice(0, end);
        }
    }
    return bytes;
}

export function isNumeric(value) {
    return /^-?\d+$/.test(value);
}

export function getFormattedDate(dateString, USFormat) {
    if (dateString === "1970-01-01T00:00:00") {
        return null;
    }

    const date = new Date(dateString);
    const year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : `0${month}`;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : `0${day}`;

    if (USFormat) return `${year}-${month}-${day}`;

    return `${month}/${day}/${year}`;
}

export function getFormattedISODate(dateString, USFormat) {
    if (!dateString || dateString === "1970-01-01T00:00:00") {
        return null;
    }

    // Extract date components without timezone conversion
    let year, month, day;

    // Handle ISO date strings (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS...)
    if (typeof dateString === "string" && dateString.includes("-")) {
        // Split on 'T' to get just the date part, then split by '-'
        const datePart = dateString.split("T")[0];
        const parts = datePart.split("-");

        if (parts.length >= 3) {
            year = parseInt(parts[0], 10);
            month = parseInt(parts[1], 10);
            day = parseInt(parts[2], 10);
        } else {
            // Fallback to original method if parsing fails
            return getFormattedDate(dateString, USFormat);
        }
    } else {
        // Fallback to original method for non-string or non-ISO formats
        return getFormattedDate(dateString, USFormat);
    }

    // Validate the parsed components
    if (
        isNaN(year) ||
        isNaN(month) ||
        isNaN(day) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
    ) {
        return getFormattedDate(dateString, USFormat);
    }

    // Format with zero padding
    const monthStr = month.toString().padStart(2, "0");
    const dayStr = day.toString().padStart(2, "0");

    if (USFormat) {
        return `${year}-${monthStr}-${dayStr}`;
    }

    return `${monthStr}/${dayStr}/${year}`;
}

export function getURLConstantsKey(pathname) {
    const urlRegex = /\/(customer|certificate|customer-exemptions|campaigns|review)\/(\d+)/;
    return pathname?.match(urlRegex);
}

export function validateEmail(email) {
    const regEx =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    return !!email?.match(regEx);
}

export function buildURL(baseURL) {
    const { appEnv } = getLazyConfig();
    const url = new URL(appEnv === "local" ? `http://${baseURL}` : `https://${baseURL}`);
    return url.href;
}

export function buildURLWithQuery(baseURL, filter, include, top, skip, sortColumn) {
    const { appEnv } = getLazyConfig();
    const url = new URL(appEnv === "local" ? `http://${baseURL}` : `https://${baseURL}`);
    if (filter) {
        url.searchParams.append("$filter", filter);
    }
    if (include && include.length) {
        url.searchParams.append("$include", include.join());
    }
    if (top) {
        url.searchParams.append("$top", top);
    } else if (top !== 0) {
        const rowsPerPage = JSON.parse(localStorage.getItem("rowsPerPage"));
        if (rowsPerPage) {
            url.searchParams.append("$top", rowsPerPage);
        } else {
            url.searchParams.append("$top", 20);
        }
    }
    if (skip) {
        url.searchParams.append("$skip", skip);
    }
    if (baseURL.includes("/v3/") && sortColumn) {
        url.searchParams.append("$orderBy", sortColumn);
    } else if (sortColumn) {
        let [orderByParam, order] = sortColumn;
        order = order ? "asc" : "desc";
        orderByParam = `${orderByParam} ${order}`;
        url.searchParams.append("$orderBy", orderByParam);
    }
    return url.href;
}

export function buildApiV2Point5UrlWithQuery(relativeUrl, filter, include, top, skip, sortColumn) {
    const { ecmApiHost, apiVersion } = getLazyConfig();
    return buildURLWithQuery(
        `//${ecmApiHost}/api/${apiVersion}/${relativeUrl}`,
        filter,
        include,
        top,
        skip,
        sortColumn
    );
}

export function buildApiV2Point5Url(relativeUrl) {
    const { ecmApiHost, apiVersion } = getLazyConfig();
    return buildURL(`//${ecmApiHost}/api/${apiVersion}/${relativeUrl}`);
}

export function buildApiV3UrlWithQuery(relativeUrl, filter, include, top, skip, sortColumn) {
    const { ecmApiHost, apiVersion3 } = getLazyConfig();
    return buildURLWithQuery(
        `//${ecmApiHost}/api/${apiVersion3}/${relativeUrl}`,
        filter,
        include,
        top,
        skip,
        sortColumn
    );
}

export function buildApiV3Url(relativeUrl) {
    const { ecmApiHost, apiVersion3 } = getLazyConfig();
    return buildURL(`//${ecmApiHost}/api/${apiVersion3}/${relativeUrl}`);
}

export function poll(fn, retries = Infinity, timeoutBetweenAttempts = 1000) {
    return Promise.resolve()
        .then(fn)
        .catch(function retry(err) {
            if (retries - 1 > 0) return delay(timeoutBetweenAttempts).then(fn).catch(retry);
            throw err;
        });
}

export function toTitleCase(str) {
    return str?.replace(/\w\S*/g, txt => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

/**
 * Checks if an array or object has any items (non-empty).
 *
 * @param {*} value - Value to check
 * @returns {boolean} True if the value has items, false otherwise
 */
export function hasItems(value) {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (value && typeof value === "object") {
        return Object.keys(value).length > 0;
    }
    return false;
}

export function fixSearchStringForPostgres(str) {
    let searchString = str?.replace(/&/g, "%26");
    searchString = searchString?.replace(/#/g, "%23");
    return searchString?.replace(/'/g, "''");
}

export function fixSearchStringForCertificates(str) {
    return str.replace(/'/g, "''");
}

export function sanitizeAddressForDisplay(address) {
    if (!address) return "";

    return address
        .replace(/[\n\r\t\f\v]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function checkSelectComponentProps(component, props) {
    if (props.onAdd === undefined) {
        throw Error(`You must pass an onAdd function to ${component}`);
    }
    if (props.onRemove === undefined) {
        throw Error(`You must pass an onRemove function to ${component}`);
    }
}

export function sentenceCase(text, ignoreAndReturnEmpty = []) {
    if (text == null) {
        return "";
    }

    if (ignoreAndReturnEmpty?.some(value => text?.includes(value))) {
        return "";
    }

    const textWithCase = text
        ?.toLowerCase()
        ?.split(" ")
        ?.map(word => {
            let char = "";
            if (word?.includes("/")) {
                char = "/";
            }
            if (word?.includes("-")) {
                char = "-";
            }
            return char
                ? word.charAt(0).toUpperCase() +
                      word.slice(1, 1 + word.search(char)) +
                      word.charAt(1 + word.search(char)).toUpperCase() +
                      word.slice(2 + word.search(char))
                : word.charAt(0).toUpperCase() + word.slice(1);
        });
    return textWithCase?.join(" ");
}

/**
 * Returns today's date as YYYY-MM-DD in local time (for consistent default range).
 * @returns {string}
 */
export function getTodayLocalDateString() {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Returns the date N days ago as YYYY-MM-DD in local time (for consistent default range).
 * @param {number} days - Number of days ago (0 = today)
 * @returns {string}
 */
export function getLocalDateStringForDaysAgo(days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Returns the date N days from today as YYYY-MM-DD in local time (negative = past).
 * @param {number} daysToAdd
 * @returns {string}
 */
export function getLocalDateStringForDaysFromToday(daysToAdd) {
    const d = new Date();
    d.setDate(d.getDate() + daysToAdd);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function nextDays(days) {
    return getLocalDateStringForDaysFromToday(days);
}

export function getTimeFrame(timeLength) {
    const timeFrames = timeLength?.split(" ");
    const [timeDuration, timeFrame] = timeFrames;
    const isMonth = timeFrame?.match(/months/) != null;
    if (timeLength?.match(/months|days/) == null) {
        return { timeFrame: timeDuration, timeDuration: timeFrame, isMonth };
    }
    return { timeFrame, timeDuration, isMonth };
}

export function getDuration(timeLength) {
    const timeFrames = timeLength?.split(" ");
    return timeFrames?.length > 0 ? timeFrames[0] : null;
}

export function previousDays(days) {
    return getLocalDateStringForDaysAgo(days);
}

/**
 * Parses YYYY-MM-DD as local date (not UTC) and returns days ago from today. 0 = today.
 * @param {string} dateStr - ISO date string (YYYY-MM-DD)
 * @returns {number} Days ago (0 = today, 1 = yesterday, etc.)
 */
export function getDaysAgo(dateStr) {
    if (!dateStr) return 0;
    const parts = dateStr.split("-");
    if (parts.length !== 3) return 0;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return 0;
    const d = new Date(year, month, day);
    if (Number.isNaN(d.getTime())) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((today - d) / 86400000));
}

export function getdefaultValue(value) {
    switch (typeof value) {
        case "number":
            return 0;
        case "string":
            return "";
        case "boolean":
            return false;
        case "object":
            return [];
        default:
            return null;
    }
}

export function formatTemplate(content) {
    let formattedContent = content?.replaceAll("â", "&apos;");

    if (!formattedContent) return formattedContent;

    // Apply same logic as getHtmlcontent - PDF-optimized spacing
    // PRESERVE existing styles that include manual indentation (&nbsp;)
    formattedContent = formattedContent.replace(/<p([^>]*)>/g, (match, attributes) => {
        if (match.includes("{{COMPANY_LOGO}}")) {
            return `<p style="text-align:right; margin: 0; padding: 0; line-height: 1.2;">`;
        }

        // If paragraph already has proper styling (manual indentation), keep it
        if (
            attributes.includes("line-height") &&
            (attributes.includes("margin-bottom") || attributes.includes("margin:"))
        ) {
            return match; // Preserve existing manual indentation styles!
        }

        // Apply PDF-optimized spacing - use margin: 0 and smaller line-height for PDF compatibility
        const style = "margin: 0; padding: 0; line-height: 1.2;";
        const cleanAttributes = attributes
            .replace(/class="[^"]*"/gi, "")
            .replace(/style="[^"]*"/gi, "");
        return `<p${cleanAttributes} style="${style}">`;
    });

    // Remove empty paragraphs
    formattedContent = formattedContent.replace(/<p[^>]*>\s*<\/p>/g, "");

    return formattedContent;
}

export function matchSubscriptions(subscriptions, productInfo) {
    const match = subscriptions?.filter(sub => productInfo?.includes(sub));
    return match?.length > 0;
}

export function matchRole(allowedRoles, currentRole) {
    return allowedRoles?.some(item => currentRole?.includes(item));
}

export const listDifference = (list1, list2, returnIntersect = false) =>
    list1?.filter(
        (
            set => a =>
                returnIntersect === set?.has(a.id)
        )(new Set(list2?.map(b => b.id)))
    );

export const retainCriticalData = e => {
    let vendorColumnConfigData = null;

    if (localStorage.getItem("vendorColumnConfig")) {
        vendorColumnConfigData = JSON.parse(localStorage.getItem("vendorColumnConfig"));
    }
    localStorage.clear();

    if (vendorColumnConfigData)
        localStorage.setItem("vendorColumnConfig", JSON.stringify(vendorColumnConfigData));

    if (e?.detail?.url) {
        window.location = e.detail.url;
    }
};

export const clearLocalStorage = () => {
    if (localStorage.getItem(ACTIVITY_STORAGE_KEY)) {
        localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    }

    CrossTabCoordinator.cleanupCoordinationKeys();
};

/**
 * Sets a cookie with the given name, value, and expiration days.
 *
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 30)
 */
export const setCookie = (name, value, days = 30) => {
    try {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value}; ${expires}; path=/`;
    } catch (error) {
        // Handle cookie errors gracefully (e.g., private browsing, security restrictions)
        // eslint-disable-next-line no-console
        console.warn(`Failed to set cookie "${name}":`, error);
    }
};

/**
 * Gets a cookie value by name.
 *
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = name => {
    try {
        const nameEQ = `${name}=`;
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            while (cookie.charAt(0) === " ") {
                cookie = cookie.substring(1, cookie.length);
            }
            if (cookie.indexOf(nameEQ) === 0) {
                return cookie.substring(nameEQ.length, cookie.length);
            }
        }
        return null;
    } catch (error) {
        // Handle cookie errors gracefully
        // eslint-disable-next-line no-console
        console.warn(`Failed to get cookie "${name}":`, error);
        return null;
    }
};

/**
 * Removes a cookie by setting its expiration date in the past.
 *
 * @param {string} name - Cookie name
 */
export const removeCookie = name => {
    try {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } catch (error) {
        // Handle cookie errors gracefully
        // eslint-disable-next-line no-console
        console.warn(`Failed to remove cookie "${name}":`, error);
    }
};

/**
 * Clears account-specific localStorage keys when account switch occurs.
 * This ensures that data cached for one account doesn't persist when switching to another account.
 *
 * @param {string[]} keysToClear - Array of localStorage keys to clear. If not provided, uses default keys from accountSwitchStorageKeys.
 */
export const clearAccountSwitchStorageKeys = (keysToClear = null) => {
    const keys = keysToClear || ACCOUNT_SWITCH_STORAGE_KEYS;

    keys.forEach(key => {
        try {
            if (localStorage.getItem(key)) {
                localStorage.removeItem(key);
            }
        } catch (error) {
            // Handle localStorage errors gracefully (e.g., quota exceeded, private browsing)
            // eslint-disable-next-line no-console
            console.warn(`Failed to clear localStorage key "${key}" during account switch:`, error);
        }
    });
};

export const errorTag = "error";

export const noErrorTag = "no-error";

export function formatDateWithHyphen(date, includeTime = true, separator = "-") {
    return new Date(date)
        .toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(includeTime && {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }),
        })
        .replace(/\//g, separator);
}

export const formatDate = ({ date, format = "en-US", includeTime = true, separator = "-" }) => {
    return new Date(date)
        .toLocaleString(format, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(includeTime && {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }),
        })
        .replace(/\//g, separator);
};

export function removeSpaces(str) {
    return str?.replace(/ /g, "");
}

export function lowerCaseAllExceptFirst(str) {
    return `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}`;
}

export function convertFieldToString(array, field, nestedObjects) {
    if (!Array.isArray(array)) return [];
    return array.map(element => {
        const copyElement = { ...element };
        if (field && copyElement[field]) copyElement[field] = copyElement[field]?.toString();
        for (let i = 0; i < nestedObjects?.length; i += 1) {
            if (copyElement[nestedObjects[i]]?.id)
                copyElement[nestedObjects[i]].id = copyElement[nestedObjects[i]]?.id?.toString();
        }
        return copyElement;
    });
}
// Disable any hyperlink programmatically
export function disableLink(id) {
    const element = document.getElementById(id);
    if (!element.hasClickListener) {
        element.addEventListener("click", event => event.preventDefault());
        element.hasClickListener = true;
    }
}

export const getLabelForCustomField = (type, element) => {
    let fieldLabel = element;
    if (type === "boolean") {
        if (element.toUpperCase() === "Y") {
            fieldLabel = "Yes";
        } else {
            fieldLabel = "No";
        }
    }

    return fieldLabel;
};

export const isOneWebDomainRuntime = () => {
    return !!window.AvaAppConfig;
};

// function is used to transform the exemption reason name to 'Statement of taxability' instead of 'taxable'
// for matrix, drop down and grid data
export function transformExemptionData(data, isMatrix, isDropdown, keys = ["name"]) {
    const transformTaxableName = (item, key) => {
        if (item[key]?.toLowerCase() === Taxable) {
            return {
                ...item,
                [key]: StatementOfTaxability,
            };
        }
        return item;
    };
    if (isMatrix === true && isDropdown === false) {
        if (Array.isArray(data)) {
            return data.map(item => {
                // Handle items in the exemption matrix
                return item.exemptReason
                    ? {
                          ...item,
                          exemptReason: transformTaxableName(item.exemptReason, "name"),
                      }
                    : item;
            });
        }
        // Handle the single object case with expectedTaxCodeName and taxCodeName
        return {
            ...data,
            expectedTaxCodeName: transformTaxableName(data, "expectedTaxCodeName")
                .expectedTaxCodeName,
            taxCodeName: transformTaxableName(data, "taxCodeName").taxCodeName,
        };
    }
    if (isMatrix === false && isDropdown === true) {
        return data.map(item => {
            return transformTaxableName(item, "name");
        });
    }

    const deepCopy = obj => JSON.parse(JSON.stringify(obj));

    const updatedData = deepCopy(data);

    const valueArray = Array.isArray(updatedData.data?.value) ? updatedData.data.value : [];

    updatedData.data.value = valueArray.map(record => {
        let updatedRecord = { ...record };
        keys.forEach(key => {
            updatedRecord = transformTaxableName(updatedRecord, key);
        });
        return updatedRecord;
    });

    return updatedData;
}

export const getRowsPerPage = (paginateData, rowsPerPage) => {
    let rowsToReturn = rowsPerPage;
    if (paginateData.totalRecords <= 50 && rowsPerPage === 100) {
        rowsToReturn = 50;
    } else if (paginateData.totalRecords <= 20 && rowsPerPage >= 50) {
        rowsToReturn = 20;
    } else if (paginateData.totalRecords <= 10 && rowsPerPage >= 20) {
        rowsToReturn = 10;
    }

    return rowsToReturn;
};

export const groupCommunicationTaxTypesByExposureZoneId = (taxTypes, exposureZoneIds) => {
    const groupedTaxTypes = {};
    taxTypes.forEach(taxType => {
        const { exposureZoneId } = taxType;
        if (exposureZoneIds.includes(exposureZoneId)) {
            if (!groupedTaxTypes[exposureZoneId]) {
                groupedTaxTypes[exposureZoneId] = [];
            }
            groupedTaxTypes[exposureZoneId].push(taxType.id);
        }
    });
    return groupedTaxTypes;
};

export const getCustomValue = (customField, fieldName) => {
    // Input validation
    if (!customField || !fieldName) {
        return [];
    }

    // Remove duplicates
    const uniqueFields = [...new Set(customField)].filter(
        field => field !== null && field !== "null"
    );

    if (uniqueFields.length === 0) {
        return [];
    }

    // Find matching field and extract value
    const FIELD_SEPARATOR = ":";

    const matchingField = uniqueFields.find(field =>
        field.match(new RegExp(`^\\s*${RegExp.escape(fieldName)}\\s*${FIELD_SEPARATOR}`))
    );

    if (matchingField) {
        const [, value] = matchingField.split(FIELD_SEPARATOR);
        return value.trim();
    }

    return "";
};

export const getAssetURL = asset => {
    const { assetsPath } = getLazyConfig();
    return assetsPath ? `${assetsPath}/assets/${asset}` : `/assets/${asset}`;
};

const replaceCustomFieldKeyWithId = (list, obj) => {
    if (!obj) return obj;
    const result = {};

    Object.keys(obj).forEach(key => {
        const item = list.find(item => item.fieldName.toLowerCase() === key.toLowerCase());
        const newKey = item ? item.id : null;
        if (!newKey) return;
        result[newKey] = obj[key];
    });

    return result;
};

const replaceValueWithId = (list, value = []) => {
    const ids = value
        .map(_value => {
            return list.find(item => item.name.toLowerCase() === _value.toLowerCase())?.id;
        })
        .filter(Boolean);

    return ids;
};

const arrayToString = array => {
    if (!Array.isArray(array)) return "";

    return array
        .filter(Boolean) // Remove any falsy values
        .join("\n"); // Join with newline character
};

export const parseFiltersState = (filters, data, initialState = {}) => {
    let updatedFilters = { ...filters };

    // Step 1: Extract values from nested objects (e.g., {value: ["California"]} -> ["California"])
    const extractedFilters = {};
    Object.entries(updatedFilters).forEach(([key, value]) => {
        if (
            value &&
            typeof value === "object" &&
            Object.prototype.hasOwnProperty.call(value, "value")
        ) {
            extractedFilters[key] = value.value;
        } else {
            extractedFilters[key] = value;
        }
    });

    // Step 2: Apply existing logic for custom fields and value replacement
    const customFieldKey = Object.keys(extractedFilters).find(key => key.includes("CustomFields"));

    if (customFieldKey && data.customFields) {
        extractedFilters[customFieldKey] = replaceCustomFieldKeyWithId(
            data.customFields.list,
            extractedFilters[customFieldKey]
        );
    }

    Object.entries(data).forEach(([key, value]) => {
        if (value && value.list) {
            extractedFilters[key] = replaceValueWithId(value.list, extractedFilters[key]);
            if (
                [
                    "excludeCustomerLabels",
                    "certificatesLabels",
                    "excludeCertificateLabels",
                    "customersLabels",
                ]?.includes(key) &&
                extractedFilters[key].length > 0
            ) {
                // Convert string values to numbers and add them to the array
                const numericValues = extractedFilters[key]
                    .map(value => {
                        const numValue = Number(value);
                        return !isNaN(numValue) ? numValue : null;
                    })
                    .filter(value => value !== null);

                extractedFilters[key] = [...extractedFilters[key], ...numericValues];
            }
        }
    });

    Object.entries(extractedFilters).forEach(([key, value]) => {
        if (key.toLowerCase().includes("date")) {
            extractedFilters[key] = value?.split?.("T")?.[0] || value;
        }
    });

    extractedFilters["certificateIds"] = arrayToString(extractedFilters["certificateIds"]);
    extractedFilters["customerCodes"] = arrayToString(extractedFilters["customerCodes"]);

    // Step 3: Merge with initial state - add missing keys from initial state
    return { ...initialState, ...extractedFilters };
};
// Helper function to get and parse localStorage items
export const getStoredItem = key => {
    const stored = localStorage.getItem(key);
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch {
            return null;
        }
    }
    return null;
};

// Helper function to set and stringify localStorage items
export const setStoredItem = (key, value) => {
    try {
        const stringifiedValue = JSON.stringify(value);
        localStorage.setItem(key, stringifiedValue);
        return true;
    } catch {
        return false;
    }
};

export const isUndefinedOrNull = obj => {
    return typeof obj === "undefined" || obj === null;
};

export const isNullOrEmptyObject = obj => {
    return isUndefinedOrNull(obj) || Object.keys(obj).length === 0;
};

export const isNotNullOrEmptyObject = obj => {
    return !isNullOrEmptyObject(obj);
};

export const isNullOrEmptyArray = arr => {
    return isUndefinedOrNull(arr) || arr.length === 0;
};

export const isNotNullOrEmptyArray = arr => {
    return !isNullOrEmptyArray(arr);
};

export const isNullOrEmptyString = str => {
    return isUndefinedOrNull(str) || str.length === 0 || str.trim() === "";
};

export const sanitizeUrl = url => {
    try {
        const parsed = new URL(url);
        return ["http:", "https:"].includes(parsed.protocol) ? url : "#";
    } catch {
        return "#";
    }
};

export const downloadFileFromStream = ({ response, fileName }) => {
    if (response.headers["content-type"].includes("application/json")) {
        return;
    }
    const blob = new Blob([response.data], { type: response.data.type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const truncateString = (str, maxLength) => {
    if (str.length <= maxLength) {
        return str;
    }
    return str.slice(0, maxLength) + "...";
};

export const getCleanAddressPart = part => {
    if (typeof part === "string") {
        const trimmed = part.trim();
        return trimmed === "" ? null : trimmed;
    }
    return part;
};

export const formatAddress = (addressParts, country, zip) => {
    const cleanParts = addressParts.map(getCleanAddressPart).filter(Boolean);
    let result = cleanParts.join(", ");

    if (country?.initials) {
        if (cleanParts.length > 0) {
            result += `, ${country.initials}`;
        } else {
            result = country.initials;
        }
    }

    if (zip) {
        const cleanZip = getCleanAddressPart(zip);
        if (cleanZip) {
            result += ` ${cleanZip}`;
        }
    }

    return result;
};

/**
 * Applies phone number mask pattern (e.g., "999-999-9999")
 * @param {string} value - The phone number input
 * @param {string} pattern - The numeric pattern
 * @returns {string} - Formatted phone number
 */
const applyPhoneMask = (value, pattern) => {
    // Extract only digits from input
    const digits = value.replace(/\D/g, "");

    // Apply pattern formatting
    let formatted = "";
    let digitIndex = 0;

    for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
        if (pattern[i] === "9") {
            formatted += digits[digitIndex];
            digitIndex++;
        } else {
            formatted += pattern[i];
        }
    }

    return formatted;
};

/**
 * Applies phone number mask from purchaserSettings
 * @param {string} phone - The phone number to format
 * @param {Array} purchaserSettings - Array of purchaser settings
 * @returns {string} - Formatted phone number
 */
export const applyPhoneMaskFromSettings = (phone, purchaserSettings = []) => {
    if (!phone || typeof phone !== "string") return phone;

    // Ensure purchaserSettings is an array
    if (!Array.isArray(purchaserSettings)) {
        return phone;
    }

    // Find phone setting from purchaserSettings
    const phoneSetting = purchaserSettings.find(
        setting => setting.purchaserSettingName === "Phone"
    );

    // Only apply mask if phone field is enabled and has mask pattern
    if (phoneSetting?.disabled || !phoneSetting?.mask) {
        return phone;
    }

    // Apply mask pattern following legacy flow:
    // Database: ["999-999-9999"] (VARCHAR) → PHP: '["999-999-9999"]' (string) → JS: ["999-999-9999"] (array)
    try {
        let pattern = null;

        // Step 1: Check if mask is already a JavaScript array (parsed from JSON)
        if (Array.isArray(phoneSetting.mask) && phoneSetting.mask.length > 0) {
            pattern = phoneSetting.mask[0];
        }
        // Step 2: Try to parse as JSON string format: '["999-999-9999"]'
        else if (typeof phoneSetting.mask === "string") {
            // Try JSON parse first (handles: '["999-999-9999"]')
            try {
                const maskArray = JSON.parse(phoneSetting.mask);
                if (Array.isArray(maskArray) && maskArray.length > 0) {
                    pattern = maskArray[0];
                }
            } catch {
                // If JSON parse fails, check if it's a plain string pattern: "999-999-9999"
                if (phoneSetting.mask.includes("999")) {
                    pattern = phoneSetting.mask;
                }
            }
        }

        // Apply the extracted pattern
        if (pattern && typeof pattern === "string" && pattern.includes("999")) {
            return applyPhoneMask(phone, pattern);
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Error applying phone mask pattern:", error);
    }

    return phone;
};

/**
 * Validates phone number input based on client configuration
 * @param {string} phone - Phone number to validate
 * @param {boolean} hasPhoneMask - Whether client has phone mask configured
 * @param {boolean} isTyping - Whether user is currently typing (more lenient validation)
 * @returns {string|null} - Error message or null if valid
 */
export const validatePhoneInputStrict = (phone, hasPhoneMask, isTyping = false) => {
    if (!phone || typeof phone !== "string") return null;

    if (hasPhoneMask) {
        // STRICT: Must be exactly 123-456-7890 format
        const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;

        // If user is typing, be more lenient
        if (isTyping) {
            const digits = phone.replace(/\D/g, "");
            // Only show error if user has typed more than 3 characters and it's clearly wrong
            if (phone.length > 3 && digits.length > 0 && digits.length < 10) {
                return "Please enter phone number in format: 123-456-7890";
            }
            return null; // Allow typing
        }

        // Strict validation for final input
        if (!phoneRegex.test(phone)) {
            return "Please enter phone number in format: 123-456-7890";
        }

        const digits = phone.replace(/\D/g, "");
        if (digits.length !== 10) {
            return "Phone number must be exactly 10 digits";
        }

        return null; // Valid
    }
};

/**
 * Formats phone number for storage based on client configuration
 * @param {string} phone - Phone number to format
 * @param {boolean} hasPhoneMask - Whether client has phone mask configured
 * @returns {string} - Formatted phone number for storage
 */
export const formatPhoneForStorage = (phone, hasPhoneMask) => {
    if (!phone || typeof phone !== "string") return phone;

    if (hasPhoneMask) {
        // Store in strict format: 123-456-7890
        const digits = phone.replace(/\D/g, "");
        if (digits.length === 10) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return phone; // Return as-is if not 10 digits
    } else {
        // Store as-is for flexibility
        return phone;
    }
};

/**
 * Executes a callback while preserving the current scroll position.
 * Useful for preventing scroll jumps when focus changes during filter resets.
 * Uses focusin event listener to capture scroll position before focus-induced scroll.
 * Preserves both window scroll and filter content scroll positions.
 * @param {Function} callback - The function to execute
 */
export const preserveScrollPosition = callback => {
    const filterPanel = document.querySelector("s-filters-panel");
    if (!filterPanel) {
        callback();
        return;
    }

    // Store scroll positions before the operation
    const windowScrollY = window.scrollY;
    const windowScrollX = window.scrollX;

    // Also store filter content scroll position
    const filterContent = filterPanel.querySelector('[slot="filters-panel-content"]');
    const filterContentScrollTop = filterContent?.scrollTop || 0;

    // Prevent focus-induced scroll by listening to focusin events
    const preventFocusScroll = () => {
        // Restore scroll position immediately when focus changes
        window.scrollTo(windowScrollX, windowScrollY);
        if (filterContent) {
            filterContent.scrollTop = filterContentScrollTop;
        }
    };

    // Add listener before executing callback
    filterPanel.addEventListener("focusin", preventFocusScroll, true);

    callback();

    // Remove listener and restore scroll position after a short delay
    // to handle any async focus changes
    setTimeout(() => {
        filterPanel.removeEventListener("focusin", preventFocusScroll, true);
        window.scrollTo(windowScrollX, windowScrollY);
        if (filterContent) {
            filterContent.scrollTop = filterContentScrollTop;
        }
    }, 100);
};

/**
 * Helper: Checks if reason mismatches between predictions and saved data
 * @param {Object} predictions - IDP predictions
 * @param {string} savedReason - Saved exemption reason
 * @returns {boolean} - True if mismatch detected
 */
const hasReasonMismatch = (predictions, savedReason) => {
    if (!predictions?.reason || !savedReason) {
        return false;
    }
    const predictedReason = predictions.reason.toLowerCase();
    const userReason = savedReason.toLowerCase();
    return predictedReason !== userReason;
};

/**
 * Helper: Extracts predicted regions from predictions object
 * @param {Object} predictions - IDP predictions
 * @returns {Array} - Array of predicted region names (lowercase)
 */
const extractPredictedRegions = predictions => {
    const regions = [];
    if (predictions?.state) {
        regions.push(predictions.state.toLowerCase());
    }
    if (predictions?.taxIds?.length > 0) {
        predictions.taxIds.forEach(taxId => {
            if (taxId.state) {
                regions.push(taxId.state.toLowerCase());
            }
        });
    }
    return regions;
};

/**
 * Helper: Checks if regions mismatch - ALL saved regions must be in predictions
 * @param {Object} predictions - IDP predictions
 * @param {Array} savedRegions - Array of saved region names
 * @returns {boolean} - True if mismatch detected
 */
const hasRegionMismatch = (predictions, savedRegions) => {
    if (savedRegions.length === 0) {
        return false;
    }

    const predictedRegions = extractPredictedRegions(predictions);
    if (predictedRegions.length === 0) {
        return false;
    }

    // Check if ALL saved regions are in predictions
    // predictedRegion must include savedRegion (not vice versa)
    const allMatch = savedRegions.every(savedRegion =>
        predictedRegions.find(predictedRegion => predictedRegion.includes(savedRegion))
    );

    return !allMatch;
};

/**
 * Helper: Checks if a single date field mismatches
 * @param {string} predictedDate - Predicted date
 * @param {string} savedDate - Saved date
 * @returns {boolean} - True if mismatch detected
 */
const hasDateMismatch = (predictedDate, savedDate) => {
    if (!predictedDate || !savedDate) {
        return false;
    }
    const predicted = predictedDate.slice(0, 10);
    const saved = savedDate.slice(0, 10);
    return predicted !== saved;
};

/**
 * Helper: Extracts predicted expiration dates from predictions
 * @param {Object} predictions - IDP predictions
 * @returns {Array} - Array of predicted expiration dates
 */
const extractPredictedExpirationDates = predictions => {
    const dates = [];

    // Single expiration date (non-multi certificate)
    if (predictions?.expirationDate) {
        dates.push(predictions.expirationDate);
    }

    // Multiple expiration dates from taxIds (multi-jurisdictional certificate)
    if (predictions?.taxIds?.length > 0) {
        predictions.taxIds.forEach(taxId => {
            if (taxId.expirationDate) {
                dates.push(taxId.expirationDate);
            }
        });
    }

    return dates;
};

/**
 * Helper: Checks if expiration dates mismatch - ALL saved dates must be in predictions
 * @param {Object} predictions - IDP predictions
 * @param {Array} savedExpirationDates - Array of saved expiration dates
 * @returns {boolean} - True if mismatch detected
 */
const hasExpirationDateMismatch = (predictions, savedExpirationDates) => {
    if (savedExpirationDates.length === 0) {
        return false;
    }

    const predictedDates = extractPredictedExpirationDates(predictions);
    if (predictedDates.length === 0) {
        return false;
    }

    // Normalize dates to YYYY-MM-DD format for comparison
    const normalizedPredictedDates = predictedDates.map(d => d.slice(0, 10));
    const normalizedSavedDates = savedExpirationDates.map(d => d.slice(0, 10));

    // Check if ALL saved dates are in predictions
    const allDatesMatch = normalizedSavedDates.every(savedDate =>
        normalizedPredictedDates.includes(savedDate)
    );

    return !allDatesMatch;
};

/**
 * Helper: Checks if business type mismatches (exact match required)
 * @param {Object} predictions - IDP predictions
 * @param {string} savedBusinessType - Saved business type
 * @returns {boolean} - True if mismatch detected
 */
const hasBusinessTypeMismatch = (predictions, savedBusinessType) => {
    if (!predictions?.businessType || !savedBusinessType) {
        return false;
    }
    const predictedType = predictions.businessType.toLowerCase();
    const userType = savedBusinessType.toLowerCase();
    return predictedType !== userType;
};

/**
 * Helper: Extracts predicted tax IDs from predictions
 * @param {Object} predictions - IDP predictions
 * @returns {Array} - Array of predicted tax IDs (lowercase, trimmed)
 */
const extractPredictedTaxIds = predictions => {
    if (!predictions?.taxIds?.length) {
        return [];
    }
    return predictions.taxIds
        .map(t => (t.value || t.taxNumber || "").toLowerCase().trim())
        .filter(Boolean);
};

/**
 * Helper: Checks if tax IDs mismatch - ALL saved tax IDs must be in predictions
 * @param {Object} predictions - IDP predictions
 * @param {Array} savedTaxIds - Array of saved tax IDs
 * @returns {boolean} - True if mismatch detected
 */
const hasTaxIdMismatch = (predictions, savedTaxIds) => {
    if (savedTaxIds.length === 0) {
        return false;
    }

    const predictedTaxIds = extractPredictedTaxIds(predictions);
    if (predictedTaxIds.length === 0) {
        return false;
    }

    const allTaxIdsMatch = savedTaxIds.every(savedTaxId =>
        predictedTaxIds.find(
            predictedTaxId => predictedTaxId === savedTaxId || predictedTaxId.includes(savedTaxId)
        )
    );

    return !allTaxIdsMatch;
};

/**
 * Helper: Checks if PO number mismatches
 * @param {Object} predictions - IDP predictions
 * @param {string} savedPoNumber - Saved PO number
 * @returns {boolean} - True if mismatch detected
 */
const hasPoNumberMismatch = (predictions, savedPoNumber) => {
    const hasPredictedPo = predictions?.purchaseOrder;
    const hasSavedPo = savedPoNumber && savedPoNumber.trim();

    if (!hasPredictedPo || !hasSavedPo) {
        return false;
    }

    const predictedPo = (predictions.purchaseOrder || "").trim();
    const userPo = savedPoNumber.trim();
    return predictedPo !== userPo;
};

/**
 * Detects mismatch between user-entered/saved certificate data and IDP predictions.
 * Used to determine if the AI mismatch banner should be shown.
 *
 * @param {Object} params - Parameters for mismatch detection
 * @param {Object} params.predictions - IDP predictions object
 * @param {Array} params.savedRegions - Array of saved region names (lowercase)
 * @param {string} params.savedReason - Saved exemption reason (lowercase)
 * @param {string} params.savedEffectiveDate - Saved effective date (YYYY-MM-DD format)
 * @param {Array} params.savedExpirationDates - Array of saved expiration dates (YYYY-MM-DD format)
 * @param {string} params.savedBusinessType - Saved business type (lowercase)
 * @param {Array} params.savedTaxIds - Array of saved tax IDs (lowercase, trimmed)
 * @param {string} params.savedPoNumber - Saved PO number
 * @returns {boolean} - True if mismatch detected, false otherwise
 */
export const detectCertificateMismatch = ({
    predictions,
    savedRegions = [],
    savedReason = "",
    savedEffectiveDate = "",
    savedExpirationDates = [],
    savedBusinessType = "",
    savedTaxIds = [],
    savedPoNumber = "",
}) => {
    if (!predictions || Object.keys(predictions).length === 0) {
        return false;
    }

    // Check each field for mismatch using helper functions
    return (
        hasReasonMismatch(predictions, savedReason) ||
        hasRegionMismatch(predictions, savedRegions) ||
        hasDateMismatch(predictions?.effectiveDate, savedEffectiveDate) ||
        hasExpirationDateMismatch(predictions, savedExpirationDates) ||
        hasBusinessTypeMismatch(predictions, savedBusinessType) ||
        hasTaxIdMismatch(predictions, savedTaxIds) ||
        hasPoNumberMismatch(predictions, savedPoNumber)
    );
};
